import { useCallback, useContext, useEffect, useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import SuccessModal from '../components/modals/SuccessModal';
import { useNotificationModal } from '../hooks/useNotificationModal';
import DeleteModal from "../components/modals/DeleteModal";
import '../styles/Currency.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function Currency() {
  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    showError,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const { userData } = useContext(AuthContext);
  const [error, setError] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCurrencyId, setDeleteCurrencyId] = useState(null);

  const company_code = userData?.company_code;
  const user_id = userData?.id;

  // Fetch currency options from backend API
  const fetchCurrencyOptions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/currencies/get-all-currency-symbols`
      );
      if (response.data && response.data.currencies) {
        setCurrencyOptions(response.data.currencies);
      } else {
        throw new Error("Currency API returned no symbols");
      }
    } catch (err) {
      setCurrencyOptions([
        { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
        { value: "GBP", label: "GBP (£) - British Pound Sterling" },
        { value: "USD", label: "USD ($) - US Dollar" },
        { value: "EUR", label: "EUR (€) - Euro" },
        { value: "AED", label: "AED (د.إ) - UAE Dirham" },
      ]);
    }
  }, []);

  // Fetch currencies for the company
  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/currencies/get-currencies`,
        {
          params: { company_code },
        }
      );
      if (response.data.success) {
        setCurrencies(response.data.currencies);
      } else {
        setError(response.data.message || "Error fetching currencies");
      }
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to fetch currencies");
    }
  }, [company_code]);

  useEffect(() => {
    fetchCurrencies();
    fetchCurrencyOptions();
  }, [fetchCurrencies, fetchCurrencyOptions]);

  // For dropdown options from backend API
  const [currencyOptions, setCurrencyOptions] = useState([
    { value: "LKR", label: "LKR (₨) - Sri Lankan Rupee" },
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCurrency) {
      showError("Please select a currency", "Validation Error");
      return;
    }
    try {
      const url = editMode
        ? `${BASE_URL}/api/admin/currencies/update-currencies/${currentId}`
        : `${BASE_URL}/api/admin/currencies/add-currencies`;

      const payload = {
        currency_name: selectedCurrency.label,
        short_name: selectedCurrency.value,
        company_code: company_code,
        ...(editMode ? {} : { created_by: user_id }),
      };

      const response = editMode
        ? await axios.put(url, payload)
        : await axios.post(url, payload);

      if (response.data.success) {
        showSuccess(
          editMode
            ? "Currency updated successfully"
            : "Currency added successfully",
          "Success"
        );
        fetchCurrencies();
        resetForm();
      } else {
        showError(response.data.message || "Operation failed", "Error");
      }
    } catch (error) {
      console.error("Error:", error);
      showError(
        error.response?.data?.message || "Failed to save currency",
        "Error"
      );
    }
  };

  const handleEdit = (currency) => {
    setSelectedCurrency({
      value: currency.short_name,
      label: currency.currency_name,
    });
    setCurrentId(currency.currency_id);
    setEditMode(true);
  };

  const handleDeleteClick = (currencyId) => {
    setDeleteCurrencyId(currencyId);
    setShowDeleteModal(true);
  }

  const handleDeleteSuccess = () => {
    showSuccess('Currency deleted successfully', 'Success');
    fetchCurrencies();
  }

  const handleDeleteError = (error) => {
    showError(error?.message || "Error deleting currency", "Error");
  };


  const resetForm = () => {
    setSelectedCurrency(null);
    setEditMode(false);
    setCurrentId(null);
  };

  const filteredCurrencies = currencies.filter(
    (currency) =>
      currency.currency_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      currency.short_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="currency-container">
      <div className="currency-header">
        <div className="cm-header">
          <h1>Currency Management</h1>
        </div>
      </div>

      <div className="currency-form">
        {error && <div className="error-message">{error}</div>}
        <div>
          <h4 className="add-currency"> Add Currency </h4>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="">
              <Select
                options={currencyOptions}
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                placeholder="Select Currency..."
                className="select-currency"
                isClearable
                required
              />
            </div>
            <div className="col-md-4">
              <button type="submit" className="btn btn-primary">
                {editMode ? "Update" : "Add"} Currency
              </button>
              {editMode && (
                <button
                  type="button"
                  className="btn btn-secondary ms-2"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <div className="search-container">
        <input
          type="text"
          className="form-control"
          placeholder="Search currencies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="currency-table">
        <table className="table">
          <thead>
            <tr>
              <th>Currency Name</th>
              <th>Short Name</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCurrencies.map((currency) => (
              <tr key={currency.currency_id}>
                <td>{currency.currency_name}</td>
                <td>{currency.short_name}</td>
                <td className="currency-actions">
                  <FaEdit
                    className="action-icon me-2"
                    onClick={() => handleEdit(currency)}
                    title="Edit"
                    style={{ cursor: "pointer", color: "#007bff" }}
                  />
                  <FaTrash
                    className="action-icon text-danger"
                    onClick={() => handleDeleteClick(currency.currency_id)}
                    title="Delete"
                    style={{ cursor: "pointer" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        id={deleteCurrencyId}
        deleteUrl={`/api/admin/currencies/delete-currencies/:id`}
        entityLabel="currency"
        modalTitle="Delete Currency"
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteError={handleDeleteError}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        variant="success"
        modalTitle={successTitle}
        message={successMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        variant="error"
        modalTitle={errorTitle}
        message={errorMessage}
      />
    </div>
  );
}
